import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock, ExternalLink, FileText, ShieldAlert, X } from "lucide-react";
import { Badge } from "../../base/badge/badge";
import { Button } from "../../base/button/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../base/card/card";
import { Textarea } from "../../base/textarea/textarea";
import { ConfirmDialog } from "../confirm-dialog/confirm-dialog";
const STATUS_SCHEME = {
    pending: "warning",
    approved: "success",
    executed: "success",
    rejected: "error",
    overridden: "error",
    withdrawn: "neutral"
};
/**
 * Freigabe: one card everywhere (plan 053) — the request, what was frozen
 * for review, every reviewer with their word, and the caller's actions.
 * Which actions show is the server's call (`canDecide` …), never the card's.
 */ export function ApprovalCard({ approval, canDecide = false, canOverride = false, canWithdraw = false, onDecide, onOverride, onWithdraw, onOpenRecord, isPending = false, className }) {
    const { t, i18n } = useTranslation();
    const [rejecting, setRejecting] = useState(false);
    const [comment, setComment] = useState("");
    const [overriding, setOverriding] = useState(false);
    const [reason, setReason] = useState("");
    const date = (iso)=>new Intl.DateTimeFormat(i18n.language, {
            dateStyle: "medium"
        }).format(new Date(iso));
    const dateTime = (iso)=>new Intl.DateTimeFormat(i18n.language, {
            dateStyle: "short",
            timeStyle: "short"
        }).format(new Date(iso));
    const approvedCount = approval.decisions.filter((d)=>d.verdict === "approved").length;
    const statusText = approval.status === "pending" ? t("approvals.card.progress", {
        count: approvedCount,
        total: approval.requiredCount
    }) : t(`approvals.status.${approval.status}`);
    const decisionOf = (id)=>approval.decisions.find((d)=>d.by.id === id);
    const { preview } = approval;
    return /*#__PURE__*/ _jsxs(Card, {
        className: className,
        children: [
            /*#__PURE__*/ _jsx(CardHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start justify-between gap-3",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-0",
                            children: [
                                /*#__PURE__*/ _jsx(CardTitle, {
                                    children: approval.label
                                }),
                                /*#__PURE__*/ _jsxs(CardDescription, {
                                    children: [
                                        t("approvals.card.requestedBy", {
                                            name: approval.requestedBy.name,
                                            date: date(approval.requestedAt)
                                        }),
                                        approval.status === "pending" && approval.dueAt ? ` · ${t("approvals.card.due", {
                                            date: date(approval.dueAt)
                                        })}` : ""
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Badge, {
                            colorScheme: STATUS_SCHEME[approval.status],
                            shape: "pill",
                            children: statusText
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(CardContent, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-5",
                    children: [
                        /*#__PURE__*/ _jsxs("section", {
                            "aria-label": preview.title,
                            className: "flex flex-col gap-3",
                            children: [
                                /*#__PURE__*/ _jsx("h4", {
                                    className: "text-sm font-semibold",
                                    children: preview.title
                                }),
                                preview.facts.length > 0 && /*#__PURE__*/ _jsx("dl", {
                                    className: "grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-1 text-sm",
                                    children: preview.facts.map((fact)=>/*#__PURE__*/ _jsxs("div", {
                                            className: "contents",
                                            children: [
                                                /*#__PURE__*/ _jsx("dt", {
                                                    className: "text-muted-foreground",
                                                    children: fact.label
                                                }),
                                                /*#__PURE__*/ _jsx("dd", {
                                                    children: fact.value
                                                })
                                            ]
                                        }, fact.label))
                                }),
                                preview.document && /*#__PURE__*/ _jsxs("a", {
                                    href: preview.document.url,
                                    target: "_blank",
                                    rel: "noreferrer",
                                    className: "inline-flex w-fit items-center gap-2 text-sm text-blue-primary hover:underline",
                                    children: [
                                        /*#__PURE__*/ _jsx(FileText, {
                                            className: "size-4",
                                            "aria-hidden": true
                                        }),
                                        preview.document.name
                                    ]
                                }),
                                preview.changes && preview.changes.length > 0 && /*#__PURE__*/ _jsx("ul", {
                                    className: "flex flex-col gap-1 text-sm",
                                    children: preview.changes.map((change)=>/*#__PURE__*/ _jsxs("li", {
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-muted-foreground",
                                                    children: [
                                                        change.label,
                                                        ":",
                                                        " "
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "line-through",
                                                    children: change.before
                                                }),
                                                " →",
                                                " ",
                                                change.after
                                            ]
                                        }, change.label))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            "aria-label": t("approvals.card.reviewers"),
                            className: "flex flex-col gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("h4", {
                                    className: "text-sm font-semibold",
                                    children: t("approvals.card.reviewersNeeded", {
                                        count: approval.requiredCount,
                                        total: approval.reviewers.length
                                    })
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: "flex flex-col gap-2",
                                    children: approval.reviewers.map((reviewer)=>{
                                        const decision = decisionOf(reviewer.id);
                                        return /*#__PURE__*/ _jsxs("li", {
                                            className: "flex items-start gap-2 text-sm",
                                            children: [
                                                decision?.verdict === "approved" ? /*#__PURE__*/ _jsx(Check, {
                                                    className: "mt-0.5 size-4 text-positive",
                                                    "aria-hidden": true
                                                }) : decision?.verdict === "rejected" ? /*#__PURE__*/ _jsx(X, {
                                                    className: "mt-0.5 size-4 text-danger",
                                                    "aria-hidden": true
                                                }) : /*#__PURE__*/ _jsx(Clock, {
                                                    className: "mt-0.5 size-4 text-muted-foreground",
                                                    "aria-hidden": true
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "min-w-0",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium",
                                                            children: reviewer.name
                                                        }),
                                                        " ",
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-muted-foreground",
                                                            children: decision ? t(`approvals.card.verdict.${decision.verdict}`, {
                                                                date: dateTime(decision.at)
                                                            }) : t("approvals.card.verdict.open")
                                                        }),
                                                        decision?.comment && /*#__PURE__*/ _jsxs("p", {
                                                            className: "text-muted-foreground",
                                                            children: [
                                                                "„",
                                                                decision.comment,
                                                                "“"
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, reviewer.id);
                                    })
                                })
                            ]
                        }),
                        approval.override && /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start gap-2 rounded-md border border-danger-line bg-danger-surface p-3 text-sm text-danger",
                            children: [
                                /*#__PURE__*/ _jsx(ShieldAlert, {
                                    className: "mt-0.5 size-4",
                                    "aria-hidden": true
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "font-medium",
                                            children: t("approvals.card.overriddenBy", {
                                                name: approval.override.by.name,
                                                date: dateTime(approval.override.at)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "„",
                                                approval.override.reason,
                                                "“"
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        rejecting && /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-col gap-2",
                            children: /*#__PURE__*/ _jsx(Textarea, {
                                "aria-label": t("approvals.card.rejectReason"),
                                placeholder: t("approvals.card.rejectReasonPlaceholder"),
                                value: comment,
                                onChange: (e)=>setComment(e.target.value),
                                autoFocus: true
                            })
                        })
                    ]
                })
            }),
            (canDecide || canWithdraw || canOverride || onOpenRecord) && /*#__PURE__*/ _jsx(CardFooter, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex w-full flex-wrap items-center gap-2",
                    children: [
                        canDecide && !rejecting && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    size: "small",
                                    onClick: ()=>onDecide?.("approved", ""),
                                    disabled: isPending,
                                    children: t("approvals.card.approve")
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    size: "small",
                                    variant: "outline",
                                    onClick: ()=>setRejecting(true),
                                    disabled: isPending,
                                    children: t("approvals.card.reject")
                                })
                            ]
                        }),
                        canDecide && rejecting && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    size: "small",
                                    variant: "destructive",
                                    onClick: ()=>onDecide?.("rejected", comment.trim()),
                                    disabled: isPending || !comment.trim(),
                                    children: t("approvals.card.confirmReject")
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    size: "small",
                                    variant: "ghost",
                                    onClick: ()=>setRejecting(false),
                                    children: t("common.cancel")
                                })
                            ]
                        }),
                        canWithdraw && /*#__PURE__*/ _jsx(Button, {
                            size: "small",
                            variant: "outline",
                            onClick: onWithdraw,
                            disabled: isPending,
                            children: t("approvals.card.withdraw")
                        }),
                        onOpenRecord && /*#__PURE__*/ _jsxs(Button, {
                            size: "small",
                            variant: "ghost",
                            onClick: onOpenRecord,
                            children: [
                                /*#__PURE__*/ _jsx(ExternalLink, {
                                    "aria-hidden": true
                                }),
                                t("approvals.card.openRecord")
                            ]
                        }),
                        canOverride && /*#__PURE__*/ _jsx(Button, {
                            size: "small",
                            variant: "link",
                            className: "ml-auto text-danger",
                            onClick: ()=>setOverriding(true),
                            disabled: isPending,
                            children: t("approvals.card.override")
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmDialog, {
                open: overriding,
                onOpenChange: setOverriding,
                icon: /*#__PURE__*/ _jsx(ShieldAlert, {}),
                iconTone: "danger",
                variant: "danger",
                title: t("approvals.override.title"),
                description: t("approvals.override.warning"),
                confirmLabel: t("approvals.override.confirm"),
                isConfirmDisabled: !reason.trim(),
                isPending: isPending,
                onConfirm: ()=>{
                    onOverride?.(reason.trim());
                    setOverriding(false);
                },
                children: /*#__PURE__*/ _jsx(Textarea, {
                    "aria-label": t("approvals.override.reason"),
                    placeholder: t("approvals.override.reasonPlaceholder"),
                    value: reason,
                    onChange: (e)=>setReason(e.target.value)
                })
            })
        ]
    });
}

//# sourceMappingURL=approval-card.js.map